'use client';

/**
 * Apply Flow V2 - Refactored version using extracted hook
 * Created: January 23, 2026
 * 
 * This version uses useApplyFlow hook for state management.
 */

import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { TenantLogo } from '@/components/ui/tenant-logo';
import {
  User, ArrowRight, Loader2, X, Upload,
  FileText, Sparkles, Send, Clock, Briefcase, Mail, ChevronDown, ChevronRight,
  CheckCircle, MapPin, DollarSign, Settings2
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CurrencyInput } from '@/components/ui/currency-input';
const PlacesAutocomplete = dynamic(() => import('@/components/ui/places-autocomplete').then(mod => mod.PlacesAutocomplete), { ssr: false });
const LocationMultiSelect = dynamic(() => import('@/components/ui/location-multiselect').then(mod => mod.LocationMultiSelect), { ssr: false });
import { DatePicker } from '@/components/ui/date-picker';
import { LinkedInLoginButton, GoogleLoginButton, GitHubLoginButton } from '@/components/auth/oauth-buttons';
import { DIAL_CODES, sanitizeNationalNumber, isValidNationalNumber } from '@/lib/phone';
import { ReturningResumeDrawer } from './returning-resume-drawer';
import { formatRelativeDate, toEpochMs } from '@/lib/format';

// Import from extracted files
import { useApplyFlow } from './hooks/useApplyFlow';
import type { Question } from './types';

// Category metadata for profile-question section headers (May 12, 2026).
// Order mirrors UX flow: availability filters first, optional demographics last.
// Any question with a category outside this list falls into an "Other" bucket below.
const PLATFORM_CATEGORY_META: Record<string, { label: string; Icon: typeof User }> = {
  availability: { label: 'Availability', Icon: Clock },
  compensation: { label: 'Compensation', Icon: DollarSign },
  location: { label: 'Location', Icon: MapPin },
  preferences: { label: 'Work Preferences', Icon: Settings2 },
  personal: { label: 'Personal Details', Icon: User },
};
const PLATFORM_CATEGORY_ORDER = ['availability', 'compensation', 'location', 'preferences', 'personal'] as const;

export function ApplyFlowV2({ jobId }: { jobId: string }) {
  // All state management via hook
  const flow = useApplyFlow(jobId);

  // Destructure commonly used values
  const {
    tenant, isTenantDomain, tenantLoading, authLoading,
    step, setStep, magicLinkSent, setMagicLinkSent,
    isLoading, error, setError, isBackgroundProcessing,
    email, setEmail, fullName, setFullName, phone, setPhone, dialCode, setDialCode, formatMobile,
    job, resumeFile, setResumeFile, parsedData,
    // Returning candidate state (Feb 11, 2026 - Issue 1 fix)
    isReturningCandidate, existingResumeId, canReuseExistingResume, existingResumeCreatedAt, existingResumeFresh, platformAnswersFresh, findMissingRequired,
    alreadyApplied, existingApplicationId,
    // Auto-skip capture state (May 6, 2026)
    autoSkippedCapture, enterCaptureForResumeReplace,
    sessionId, matchAnalysis,
    platformQuestions, tenantQuestions, jobQuestions, aiQuestions,
    answers, setAnswers,
    currentAiQuestionIndex, aiQuestionTimeLeft, handleAiAnswer,
    acceptedTerms, setAcceptedTerms,
    expandedSections, toggleSection,
    answeredPlatform, platformPercentage,
    totalSteps,
    getCurrentStepNumber, getOverallProgress, buildStepNames,
    handleSendMagicLink, handleStartApplication, handleSubmit,
    handleQuestionsNext, handleJobQuestionsNext, handleSkipPlatformQuestions, formatAnswerValue,
  } = flow;

  const overallProgress = getOverallProgress();
  const currentStepNumber = getCurrentStepNumber();
  const stepNames = buildStepNames();

  // Returning-candidate "skip" card (June 2026 fix). The card may only claim
  // "your answers are up to date" + offer Skip when there is genuinely NO
  // missing required answer; otherwise a required-but-empty field (e.g. an
  // un-prefilled "willing to relocate") made Skip & Continue silently no-op.
  // "Review & Update" dismisses the card so the candidate edits the questions
  // rendered below (previously it had no handler at all).
  const [reviewingPrefill, setReviewingPrefill] = useState(false);
  const platformRequiredComplete =
    findMissingRequired([...platformQuestions, ...tenantQuestions]).length === 0;
  const showPrefillSkipCard =
    platformAnswersFresh && isReturningCandidate && platformRequiredComplete && !reviewingPrefill;

  // Issue C (June 2026): candidate already applied to this job. Show a notice
  // offering to view the existing application; "Update my answers" dismisses it
  // and lets them edit + re-submit (re-submit updates the existing application).
  const [alreadyAppliedDismissed, setAlreadyAppliedDismissed] = useState(false);
  const showAlreadyApplied = alreadyApplied && !alreadyAppliedDismissed;
  const viewExistingApplication = () => {
    if (existingApplicationId) {
      try { localStorage.setItem(`application_id_${jobId}`, String(existingApplicationId)); } catch { /* ignore */ }
    }
  };

  // Returning-candidate resume staleness nudge (2026-06-23). Every input is
  // optional: when the backend hasn't shipped createdAt/resumeFresh yet (deploy
  // order is backend→frontend) we render no date and no nudge — never crash.
  const resumeUploadedMs = toEpochMs(existingResumeCreatedAt);
  const resumeUploadedLabel = resumeUploadedMs != null ? formatRelativeDate(resumeUploadedMs) : null;
  const resumeMonthsOld = resumeUploadedMs != null
    ? Math.floor((Date.now() - resumeUploadedMs) / (30 * 24 * 60 * 60 * 1000))
    : null;
  // Stale when the backend flags it, or — flag absent — when the visible upload
  // age exceeds the 6-month window the backend uses for freshness.
  const resumeIsStale =
    existingResumeFresh === false ||
    (existingResumeFresh === undefined && resumeMonthsOld !== null && resumeMonthsOld >= 6);
  const resumeAgeLabel = (() => {
    if (resumeMonthsOld == null) return null;
    if (resumeMonthsOld >= 12) {
      const years = Math.floor(resumeMonthsOld / 12);
      return `${years} ${years === 1 ? 'year' : 'years'} old`;
    }
    if (resumeMonthsOld >= 1) return `${resumeMonthsOld} months old`;
    return null;
  })();

  // Loading state
  if (authLoading || tenantLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Resume banner shown on auto-skipped landing steps (May 6, 2026)
  // Lets a recognized candidate confirm "we're using your existing resume" with
  // a one-click Replace affordance back into the capture step. Only renders when
  // the candidate auto-skipped capture AND still has an existing resume on file.
  const ResumeBanner = () =>
    autoSkippedCapture && canReuseExistingResume ? (
      <Card className="mb-4 border-green-500/30 bg-green-500/5">
        <CardContent className="p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-green-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground leading-tight">Using your existing resume</p>
              <p className="text-sm font-medium leading-tight truncate">On file with VHire</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={enterCaptureForResumeReplace}
            className="flex-shrink-0"
          >
            <Upload className="w-3 h-3 mr-1" /> Replace
          </Button>
        </CardContent>
      </Card>
    ) : null;

  // Header component with integrated progress
  const Header = () => (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-lg mx-auto px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Link
              href="/"
              aria-label="Close application and return home"
              className="text-muted-foreground hover:text-foreground"
            >
              <X aria-hidden="true" className="w-5 h-5" />
            </Link>
            {isTenantDomain && tenant?.branding?.logo_url ? (
              <TenantLogo light={tenant.branding.logo_url} dark={tenant.branding.logo_dark_url} alt={tenant.name || 'Logo'} width={100} height={28} className="h-7 w-auto" />
            ) : (
              <span className="font-bold text-primary">VHire</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Step {currentStepNumber}/{totalSteps}</span>
            <Badge variant="secondary" className="text-xs font-bold">{overallProgress}%</Badge>
          </div>
        </div>
        {/* Progress bar */}
        <div className="flex items-center gap-1">
          {stepNames.map((name, idx) => (
            <div key={idx} className="flex-1">
              <div className={`h-1.5 rounded-full transition-all duration-300 ${
                idx + 1 < currentStepNumber
                  ? 'bg-green-500'
                  : idx + 1 === currentStepNumber
                    ? 'bg-primary'
                    : 'bg-muted'
              }`} />
              <p className={`text-[10px] mt-1 text-center truncate ${
                idx + 1 === currentStepNumber ? 'text-primary font-medium' : 'text-muted-foreground'
              }`}>
                {idx + 1 < currentStepNumber ? '✓' : ''} {name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </header>
  );

  // Render question input based on type
  const renderQuestionInput = (question: Question) => {
    const value = answers[question.id] || '';
    // ✅ When the parent answer changes, clear any conditional sub-field answers
    // whose `showIf` no longer matches — prevents stale data from sneaking through
    // (e.g., "Last Working Day" lingering after "serving notice?" flipped to No).
    const setValue = (v: string) => setAnswers(prev => {
      const next: Record<string, string> = { ...prev, [question.id]: v };
      const conditionals = [
        ...(question.conditionalFields || []),
        ...(question.conditionalField ? [question.conditionalField] : []),
      ];
      for (const cf of conditionals) {
        if (cf.showIf !== v) {
          const childId = cf.fieldId || `${question.id}_conditional`;
          if (next[childId] !== undefined) delete next[childId];
        }
      }
      return next;
    });

    switch (question.type) {
      case 'yes_no':
        return (
          <RadioGroup value={value} onValueChange={setValue} className="flex gap-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id={`${question.id}-yes`} />
              <Label htmlFor={`${question.id}-yes`} className="cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id={`${question.id}-no`} />
              <Label htmlFor={`${question.id}-no`} className="cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        );

      case 'date':
        return (
          <DatePicker
            date={value ? new Date(value) : undefined}
            onDateChange={(date) => {
              if (date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setValue(`${year}-${month}-${day}`);
              } else {
                setValue('');
              }
            }}
            showDropdowns={true}
            startYear={1960}
            endYear={new Date().getFullYear()}
          />
        );

      case 'currency':
        return (
          <CurrencyInput
            value={value}
            onChange={setValue}
            placeholder="e.g. 15 (means ₹15 Lakhs)"
          />
        );

      case 'numeric':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter number"
            className="w-full"
          />
        );

      case 'phone':
        return (
          <div className="flex">
            <span className="inline-flex items-center px-3 text-sm bg-muted border border-r-0 rounded-l-md">+91</span>
            <Input
              type="tel"
              value={formatMobile(value)}
              onChange={(e) => setValue(formatMobile(e.target.value))}
              placeholder="98765 43210"
              className="rounded-l-none"
              maxLength={10}
            />
          </div>
        );

      case 'places':
      case 'pincode':
        return (
          <PlacesAutocomplete
            purpose="candidate"
            value={value}
            onChange={(v: string, place?: { formattedAddress: string; city?: string; state?: string; country?: string; lat: number; lng: number }) => {
              // Store the full place data as JSON if available
              if (place) {
                setValue(JSON.stringify({
                  address: place.formattedAddress,
                  city: place.city,
                  state: place.state,
                  country: place.country,
                  lat: place.lat,
                  lng: place.lng
                }));
              } else {
                setValue(v);
              }
            }}
            placeholder="Search your city or area..."
            showNearbyHint={true}
          />
        );

      case 'dropdown':
      case 'multiple_choice':
        if (question.options && question.options.length > 0) {
          return (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {question.options.map((opt, i) => (
                  <SelectItem key={i} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        return <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Enter your answer" />;

      case 'checkbox_group':
        if (question.options && question.options.length > 0) {
          const selected = value ? JSON.parse(value) : [];
          return (
            <div className="space-y-2">
              {question.options.map((opt, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${question.id}-${i}`}
                    checked={selected.includes(opt)}
                    onCheckedChange={(checked) => {
                      const newSelected = checked
                        ? [...selected, opt]
                        : selected.filter((s: string) => s !== opt);
                      setValue(JSON.stringify(newSelected));
                    }}
                  />
                  <Label htmlFor={`${question.id}-${i}`} className="cursor-pointer">{opt}</Label>
                </div>
              ))}
            </div>
          );
        }
        return <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Enter your answer" />;

      case 'location_multiselect':
        return (
          <LocationMultiSelect
            value={value ? JSON.parse(value) : []}
            onChange={(locs) => setValue(JSON.stringify(locs))}
            placeholder="Select preferred locations (Bangalore, Pune, etc.)"
          />
        );

      case 'ai_mcq':
      case 'ai_prompt':
        if (question.options && question.options.length > 0) {
          return (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3 text-xs text-purple-600">
                <Sparkles className="w-3 h-3" />
                <span>AI-Powered Assessment</span>
              </div>
              {question.options.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setValue(opt)}
                  className={`w-full p-3 text-left rounded-lg border-2 transition-all ${
                    value === opt
                      ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                      : 'border-muted hover:border-purple-300 hover:bg-purple-50/50'
                  }`}
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 mr-3 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
                    {String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                </button>
              ))}
            </div>
          );
        }
        return <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Enter your answer" />;

      case 'text':
      default:
        return (
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Type your answer..."
            className="min-h-[100px]"
          />
        );
    }
  };

  // ==================== AUTH STEP ====================
  if (step === 'auth') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 pb-24 px-4">
          <div className="max-w-lg mx-auto space-y-4">
            {/* Job Card */}
            {job ? (
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0">
                      {job.company.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold">{job.title}</h2>
                      <p className="text-slate-300 text-sm">{job.company}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="secondary" className="bg-white/10 text-white text-xs">📍 {job.location}</Badge>
                        {job.experience && <Badge variant="secondary" className="bg-white/10 text-white text-xs">💼 {job.experience}</Badge>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 animate-pulse">
                    <div className="w-12 h-12 bg-slate-700 rounded-xl flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-slate-700 rounded w-3/4" />
                      <div className="h-4 bg-slate-700 rounded w-1/2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Login Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" /> Sign in to Apply
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {magicLinkSent ? (
                  <div className="text-center py-6 space-y-4">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Check your email</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        We sent a login link to <span className="font-medium">{email}</span>
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Click the link in your email to continue with your application.
                      The link expires in 15 minutes.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setMagicLinkSent(false);
                        setEmail('');
                      }}
                    >
                      Use a different email
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Sign in to continue with your application. No password needed.
                    </p>

                    {/* Social login — lead with LinkedIn, then Google, then GitHub */}
                    <div className="flex flex-col gap-3">
                      <LinkedInLoginButton
                        redirectUrl={`/apply/${jobId}`}
                        jobId={jobId}
                        tenantSlug={tenant?.slug}
                      />
                      <GoogleLoginButton
                        redirectUrl={`/apply/${jobId}`}
                        jobId={jobId}
                        tenantSlug={tenant?.slug}
                      />
                      <GitHubLoginButton
                        redirectUrl={`/apply/${jobId}`}
                        jobId={jobId}
                        tenantSlug={tenant?.slug}
                      />
                    </div>

                    <div className="relative my-2">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="auth-email" className="text-sm">Email Address</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="auth-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSendMagicLink()}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {error && (
                      <p
                        role="alert"
                        aria-live="polite"
                        className="text-sm text-destructive"
                      >
                        {error}
                      </p>
                    )}

                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={handleSendMagicLink}
                      disabled={isLoading || !email}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Continue with Email
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-center text-muted-foreground px-4">
              By continuing, you agree to VHire&apos;s Terms of Service and Privacy Policy.
            </p>
          </div>
        </main>
      </div>
    );
  }

  // ==================== CAPTURE STEP ====================
  if (step === 'capture') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 pb-24 px-4">
          <div className="max-w-lg mx-auto space-y-4">
            {/* Job Card */}
            {job ? (
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0">
                      {job.company.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold">{job.title}</h2>
                      <p className="text-slate-300 text-sm">{job.company}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="secondary" className="bg-white/10 text-white text-xs">📍 {job.location}</Badge>
                        {job.experience && <Badge variant="secondary" className="bg-white/10 text-white text-xs">💼 {job.experience}</Badge>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 animate-pulse">
                    <div className="w-12 h-12 bg-slate-700 rounded-xl flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-slate-700 rounded w-3/4" />
                      <div className="h-4 bg-slate-700 rounded w-1/2" />
                      <div className="flex gap-2 mt-2">
                        <div className="h-5 bg-slate-700 rounded w-20" />
                        <div className="h-5 bg-slate-700 rounded w-16" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Your Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4" /> Your Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-sm">Email Address *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName" className="text-sm">Full Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="fullName"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Mobile — dial code + national number, stored as E.164.
                    India (+91) stays the default; other countries unblock
                    international applicants instead of forcing a +91 prefix. */}
                <div>
                  <Label htmlFor="mobile" className="text-sm">Mobile Number *</Label>
                  <div className="flex mt-1">
                    <select
                      aria-label="Country dial code"
                      value={dialCode}
                      onChange={(e) => setDialCode(e.target.value)}
                      className="rounded-l-md border border-r-0 border-input bg-muted px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {DIAL_CODES.map((d) => (
                        <option key={d.code} value={d.code}>{d.flag} {d.code}</option>
                      ))}
                    </select>
                    <Input
                      id="mobile"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      placeholder={dialCode === '+91' ? '98765 43210' : 'Mobile number'}
                      value={phone}
                      onChange={(e) => setPhone(sanitizeNationalNumber(e.target.value))}
                      className="rounded-l-none"
                      maxLength={dialCode === '+91' ? 10 : 14}
                    />
                  </div>
                  {/* Inline validity feedback so an invalid number is caught BEFORE
                      the user clicks Continue (the button gate also enforces this). */}
                  {phone.length > 0 && !isValidNationalNumber(dialCode, phone) && (
                    <p className="text-xs text-destructive mt-1">
                      {dialCode === '+91'
                        ? 'Enter a valid 10-digit mobile number.'
                        : 'Enter a valid mobile number.'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Resume Upload - Shows existing resume option for returning candidates (Feb 11, 2026) */}
            {canReuseExistingResume && !resumeFile ? (
              <Card className="border-2 border-green-500 bg-green-500/5">
                <CardContent className="p-8 text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-green-500/10">
                    <FileText className="w-7 h-7 text-green-500" />
                  </div>
                  <p className="font-medium mb-1">Using Your Existing Resume</p>
                  <p className="text-sm text-muted-foreground mb-1">We already have your resume on file</p>
                  {resumeUploadedLabel && (
                    <p className="text-xs text-muted-foreground mb-4">Uploaded {resumeUploadedLabel}</p>
                  )}

                  {/* Staleness nudge — only when the resume is old (or the
                      backend flagged it stale). The CTA reuses the replace file
                      input below. Absent age fields → this block never renders. */}
                  {resumeIsStale && (
                    <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {resumeAgeLabel
                          ? `Your resume is ${resumeAgeLabel} — `
                          : 'Your resume may be out of date — '}
                        <label htmlFor="returning-resume-replace" className="font-medium underline cursor-pointer">
                          upload a fresh copy?
                        </label>
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {existingResumeId && (
                      <ReturningResumeDrawer resumeId={existingResumeId} />
                    )}
                    <label className={`${buttonVariants({ variant: 'outline', size: 'sm' })} cursor-pointer`}>
                      <Upload className="w-4 h-4" /> Upload a new resume
                      <input
                        id="returning-resume-replace"
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 10 * 1024 * 1024) {
                              setError('File too large. Max 10MB.');
                              return;
                            }
                            setResumeFile(file);
                            setError(null);
                          }
                        }}
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <label className="block cursor-pointer">
                <Card className={`border-2 border-dashed transition-colors ${resumeFile ? 'border-green-500 bg-green-500/5' : 'hover:border-primary'}`}>
                  <CardContent className="p-8 text-center">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 10 * 1024 * 1024) {
                            setError('File too large. Max 10MB.');
                            return;
                          }
                          setResumeFile(file);
                          setError(null);
                        }
                      }}
                    />
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${resumeFile ? 'bg-green-500/10' : 'bg-primary/10'}`}>
                      {resumeFile ? <FileText className="w-7 h-7 text-green-500" /> : <Upload className="w-7 h-7 text-primary" />}
                    </div>
                    <p className="font-medium mb-1">{resumeFile ? resumeFile.name : 'Upload Your Resume'}</p>
                    <p className="text-sm text-muted-foreground">{resumeFile ? 'Click to change' : 'PDF or Word, max 10MB'}</p>
                  </CardContent>
                </Card>
              </label>
            )}

            {error && (
              <p
                role="alert"
                aria-live="polite"
                className="text-sm text-destructive text-center"
              >
                {error}
              </p>
            )}
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="max-w-lg mx-auto">
            <Button className="w-full" size="lg" onClick={handleStartApplication} disabled={!email || !fullName || !isValidNationalNumber(dialCode, phone) || (!resumeFile && !canReuseExistingResume) || isLoading}>
              {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Continue to Questions <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== QUESTIONS STEP ====================
  if (step === 'questions') {
    const questionsOnThisScreen = [...platformQuestions, ...tenantQuestions];
    const answeredCount = questionsOnThisScreen.filter(q => answers[q.id]?.trim()).length;
    const progressPercent = questionsOnThisScreen.length > 0 ? Math.round((answeredCount / questionsOnThisScreen.length) * 100) : 0;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 pb-24 px-4">
          <div className="max-w-lg mx-auto">
            <ResumeBanner />

            {/* Phase 3 — no upload-time match score. The skill-match + score
                report is shown after submit on the success page. */}

            {/* Progress */}
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {questionsOnThisScreen.length - answeredCount} questions left
              </span>
              <span className="font-medium">{answeredCount}/{questionsOnThisScreen.length}</span>
            </div>
            <Progress value={progressPercent} className="h-2 mb-6" />

            {/* Already applied to this job (issue C, June 2026) — offer to view
                the existing application instead of silently re-applying. */}
            {showAlreadyApplied && (
              <Card className="mb-4 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800">
                      <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-blue-800 dark:text-blue-200">You&apos;ve already applied to this role</p>
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                        Your application is on file. You can view it, or update your answers below and re-submit.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Link href={`/apply/${jobId}/success`} onClick={viewExistingApplication}>
                          <Button size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700">
                            View your application
                          </Button>
                        </Link>
                        <Button size="sm" variant="outline" onClick={() => setAlreadyAppliedDismissed(true)} className="border-blue-300 text-blue-700 hover:bg-blue-100">
                          Update my answers
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Skip option for returning candidates with fresh answers (Feb 11, 2026;
                gated on platformRequiredComplete + Review & Update wired — June 2026) */}
            {showPrefillSkipCard && (
              <Card className="mb-4 border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-green-100 dark:bg-green-800">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-green-800 dark:text-green-200">Your answers are up to date</p>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        We have your profile information from your previous applications. You can skip these questions or update them below.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="default" onClick={handleSkipPlatformQuestions} className="bg-green-600 hover:bg-green-700">
                          Skip & Continue
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReviewingPrefill(true)} className="border-green-300 text-green-700 hover:bg-green-100">
                          Review & Update
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Questions */}
            <div className="space-y-4">
              {/* Platform Questions — grouped by category (May 12, 2026) */}
              {platformQuestions.length > 0 && (() => {
                const knownCats = new Set<string>(PLATFORM_CATEGORY_ORDER);
                const sections: { key: string; label: string; Icon: typeof User; questions: Question[] }[] = [
                  ...PLATFORM_CATEGORY_ORDER.map(cat => ({
                    key: cat,
                    label: PLATFORM_CATEGORY_META[cat].label,
                    Icon: PLATFORM_CATEGORY_META[cat].Icon,
                    questions: platformQuestions.filter(q => (q.category || 'personal') === cat),
                  })),
                ];
                const otherQs = platformQuestions.filter(q => !knownCats.has(q.category || 'personal'));
                if (otherQs.length > 0) {
                  sections.push({ key: 'other', label: 'Other', Icon: User, questions: otherQs });
                }
                const visible = sections.filter(s => s.questions.length > 0);
                return visible.map((section, idx) => (
                  <div key={section.key}>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                      <section.Icon className="w-4 h-4" /> {section.label} ({section.questions.length})
                      {idx === 0 && platformAnswersFresh && <span className="text-xs text-green-600 ml-2">✓ Pre-filled</span>}
                    </h3>
                    <div className="space-y-3">
                      {section.questions.map((q) => (
                        <Card key={q.id} className={answers[q.id]?.trim() ? 'border-green-500/50' : ''}>
                          <CardContent className="p-4">
                            <Label className="text-sm font-medium mb-2 block">
                              {q.text} {q.required && <span className="text-destructive">*</span>}
                            </Label>
                            {renderQuestionInput(q)}
                            {/* Conditional fields */}
                            {q.conditionalFields?.map((cf) => {
                              if (answers[q.id] !== cf.showIf) return null;
                              const fieldId = cf.fieldId || `${q.id}_conditional`;
                              return (
                                <div key={fieldId} className="mt-3 pt-3 border-t">
                                  <Label className="text-sm mb-2 block">{cf.fieldLabel}</Label>
                                  {cf.options ? (
                                    <Select value={answers[fieldId] || ''} onValueChange={(v) => setAnswers(prev => ({ ...prev, [fieldId]: v }))}>
                                      <SelectTrigger><SelectValue placeholder="Select an option" /></SelectTrigger>
                                      <SelectContent>
                                        {cf.options.map((opt, i) => <SelectItem key={i} value={opt}>{opt}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  ) : cf.fieldType === 'date' ? (
                                    <DatePicker
                                      date={answers[fieldId] ? new Date(answers[fieldId]) : undefined}
                                      onDateChange={(date) => {
                                        if (date) {
                                          const year = date.getFullYear();
                                          const month = String(date.getMonth() + 1).padStart(2, '0');
                                          const day = String(date.getDate()).padStart(2, '0');
                                          setAnswers(prev => ({ ...prev, [fieldId]: `${year}-${month}-${day}` }));
                                        } else {
                                          setAnswers(prev => ({ ...prev, [fieldId]: '' }));
                                        }
                                      }}
                                      minDate={fieldId === 'platform_last_working_day' ? new Date() : undefined}
                                      showDropdowns={fieldId !== 'platform_last_working_day'}
                                      startYear={fieldId === 'platform_last_working_day' ? new Date().getFullYear() : 1960}
                                      endYear={fieldId === 'platform_last_working_day' ? new Date().getFullYear() + 1 : new Date().getFullYear()}
                                    />
                                  ) : (
                                    <Input
                                      type="text"
                                      value={answers[fieldId] || ''}
                                      onChange={(e) => setAnswers(prev => ({ ...prev, [fieldId]: e.target.value }))}
                                    />
                                  )}
                                </div>
                              );
                            })}
                            {/* Legacy conditional field support */}
                            {!q.conditionalFields && q.conditionalField && answers[q.id] === q.conditionalField.showIf && (
                              <div className="mt-3 pt-3 border-t">
                                <Label className="text-sm mb-2 block">{q.conditionalField.fieldLabel}</Label>
                                {q.conditionalField.fieldType === 'date' ? (
                                  <DatePicker
                                    date={answers[`${q.id}_conditional`] ? new Date(answers[`${q.id}_conditional`]) : undefined}
                                    onDateChange={(date) => {
                                      if (date) {
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        setAnswers(prev => ({ ...prev, [`${q.id}_conditional`]: `${year}-${month}-${day}` }));
                                      } else {
                                        setAnswers(prev => ({ ...prev, [`${q.id}_conditional`]: '' }));
                                      }
                                    }}
                                    minDate={q.id === 'platform_notice_period' ? new Date() : undefined}
                                    startYear={q.id === 'platform_notice_period' ? new Date().getFullYear() : 1960}
                                    endYear={q.id === 'platform_notice_period' ? new Date().getFullYear() + 1 : new Date().getFullYear()}
                                  />
                                ) : (
                                  <Input
                                    type="text"
                                    value={answers[`${q.id}_conditional`] || ''}
                                    onChange={(e) => setAnswers(prev => ({ ...prev, [`${q.id}_conditional`]: e.target.value }))}
                                  />
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {/* Tenant Questions */}
              {tenantQuestions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Company Questions ({tenantQuestions.length})
                  </h3>
                  <div className="space-y-3">
                    {tenantQuestions.map((q: Question) => (
                      <Card key={q.id} className={answers[q.id]?.trim() ? 'border-green-500/50' : ''}>
                        <CardContent className="p-4">
                          <Label className="text-sm font-medium mb-2 block">
                            {q.text} {q.required && <span className="text-destructive">*</span>}
                          </Label>
                          {renderQuestionInput(q)}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="max-w-lg mx-auto">
            {/* Validation feedback (June 2026 fix) — without this, a missing
                required answer made Skip & Continue / Review & Submit appear
                to do nothing because the error set by the handler was never
                rendered in this step. */}
            {error && (
              <p role="alert" aria-live="polite" className="text-sm text-destructive text-center mb-2">
                {error}
              </p>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('capture')} disabled={isBackgroundProcessing}>Back</Button>
              <Button
                className="flex-1"
                size="lg"
                onClick={handleQuestionsNext}
                disabled={isBackgroundProcessing || !sessionId}
              >
                {isBackgroundProcessing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : jobQuestions.length > 0 ? (
                  <>Job Questions <ArrowRight className="w-4 h-4 ml-2" /></>
                ) : (
                  <>Review & Submit <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== JOB QUESTIONS STEP ====================
  if (step === 'job_questions') {
    const answeredJobCount = jobQuestions.filter(q => answers[q.id]?.trim()).length;
    const jobProgressPercent = jobQuestions.length > 0 ? Math.round((answeredJobCount / jobQuestions.length) * 100) : 0;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 pb-24 px-4">
          <div className="max-w-lg mx-auto">
            <ResumeBanner />
            {/* Job info banner */}
            {job && (
              <Card className="mb-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-4">
                  <p className="text-white/70 text-xs mb-1">JOB-SPECIFIC QUESTIONS</p>
                  <p className="font-semibold">{job.title}</p>
                  <p className="text-white/80 text-sm">{job.company}</p>
                </CardContent>
              </Card>
            )}

            {/* Progress */}
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {jobQuestions.length - answeredJobCount} questions left
              </span>
              <span className="font-medium">{answeredJobCount}/{jobQuestions.length}</span>
            </div>
            <Progress value={jobProgressPercent} className="h-2 mb-6" />

            {/* Job Questions */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Screening Questions ({jobQuestions.length})
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  These questions are specific to this position and help the hiring team understand your fit.
                </p>
                <div className="space-y-3">
                  {jobQuestions.map((q: Question) => (
                    <Card key={q.id} className={answers[q.id]?.trim() ? 'border-green-500/50' : ''}>
                      <CardContent className="p-4">
                        <Label className="text-sm font-medium mb-2 block">
                          {q.text} {q.required && <span className="text-destructive">*</span>}
                        </Label>
                        {renderQuestionInput(q)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="max-w-lg mx-auto">
            {error && (
              <p role="alert" aria-live="polite" className="text-sm text-destructive text-center mb-2">
                {error}
              </p>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('questions')}>Back</Button>
              <Button
                className="flex-1"
                size="lg"
                onClick={handleJobQuestionsNext}
              >
                {aiQuestions.length > 0 ? (
                  <>🤖 AI Quiz <ArrowRight className="w-4 h-4 ml-2" /></>
                ) : (
                  <>Review & Submit <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== AI QUESTIONS STEP ====================
  if (step === 'ai_questions') {
    const currentQuestion = aiQuestions[currentAiQuestionIndex];
    const totalAiQuestions = aiQuestions.length;
    const answeredAiCount = aiQuestions.filter(q => answers[q.id]?.trim()).length;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 pb-24 px-4">
          <div className="max-w-lg mx-auto">
            <ResumeBanner />
            {/* AI Quiz Header */}
            <Card className="mb-4 bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-xs flex items-center gap-1">
                      🤖 AI SKILL ASSESSMENT
                    </p>
                    <p className="font-semibold">Question {currentAiQuestionIndex + 1} of {totalAiQuestions}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-bold ${aiQuestionTimeLeft <= 3 ? 'text-red-300 animate-pulse' : ''}`}>
                      {aiQuestionTimeLeft}s
                    </div>
                    <p className="text-white/70 text-xs">remaining</p>
                  </div>
                </div>
                <Progress
                  value={(aiQuestionTimeLeft / (currentQuestion?.timeLimit || 10)) * 100}
                  className="h-1 mt-3 bg-white/20"
                />
              </CardContent>
            </Card>

            {/* Current Question */}
            {currentQuestion && (
              <Card className="border-2 border-purple-500/30">
                <CardContent className="p-5">
                  <div className="flex items-start gap-2 mb-4">
                    {currentQuestion.difficulty && (
                      <Badge variant="secondary" className="text-xs">
                        {currentQuestion.difficulty === 'easy' ? '🟢' : currentQuestion.difficulty === 'medium' ? '🟡' : '🔴'} {currentQuestion.difficulty}
                      </Badge>
                    )}
                    {currentQuestion.skillAssessed && (
                      <Badge variant="outline" className="text-xs">{currentQuestion.skillAssessed}</Badge>
                    )}
                  </div>
                  <p className="text-lg font-medium mb-6">{currentQuestion.text}</p>

                  {/* Options */}
                  <div className="space-y-3">
                    {currentQuestion.options?.map((opt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleAiAnswer(opt)}
                        className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                          answers[currentQuestion.id] === opt
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
                            : 'border-muted hover:border-purple-300 hover:bg-purple-50/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="inline-flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 text-sm font-bold">
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span className="break-words">{opt}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mt-6">
              {aiQuestions.map((q, idx) => (
                <div
                  key={q.id}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentAiQuestionIndex
                      ? 'w-6 bg-purple-500'
                      : answers[q.id]
                        ? 'bg-green-500'
                        : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="max-w-lg mx-auto flex gap-3">
            <Button
              variant="outline"
              onClick={() => setStep(jobQuestions.length > 0 ? 'job_questions' : 'questions')}
            >
              Back
            </Button>
            <Button
              className="flex-1"
              size="lg"
              onClick={() => setStep('review')}
              disabled={answeredAiCount < totalAiQuestions}
            >
              {answeredAiCount < totalAiQuestions
                ? `Answer ${totalAiQuestions - answeredAiCount} more`
                : 'Review & Submit'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== REVIEW STEP ====================
  if (step === 'review') {
    // Group answers by source
    const platformAnswers = Object.entries(answers).filter(([k, v]) =>
      v?.trim() && !k.includes('_conditional') && platformQuestions.some(q => q.id === k)
    );
    const tenantAnswersList = Object.entries(answers).filter(([k, v]) =>
      v?.trim() && !k.includes('_conditional') && tenantQuestions.some(q => q.id === k)
    );
    const jobAnswersList = Object.entries(answers).filter(([k, v]) =>
      v?.trim() && !k.includes('_conditional') && jobQuestions.some(q => q.id === k)
    );
    const aiAnswersList = Object.entries(answers).filter(([k, v]) =>
      v?.trim() && !k.includes('_conditional') && aiQuestions.some(q => q.id === k)
    );

    const renderCollapsibleSection = (
      title: string,
      sectionKey: string,
      answerList: [string, string][],
      questionList: Question[],
      icon?: React.ReactNode
    ) => {
      if (answerList.length === 0) return null;
      const isExpanded = expandedSections[sectionKey];

      return (
        <div key={sectionKey} className="border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection(sectionKey)}
            className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              {icon}
              <span className="font-medium text-sm">{title}</span>
              <Badge variant="secondary" className="text-xs">{answerList.length}</Badge>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          {isExpanded && (
            <div className="p-3 space-y-3 bg-background">
              {answerList.map(([key, value]) => {
                const q = questionList.find(q => q.id === key);
                const formattedValue = formatAnswerValue(value);
                return (
                  <div key={key} className="text-sm">
                    <p className="text-muted-foreground text-xs mb-0.5">{q?.text || key}</p>
                    <p className="font-medium">{formattedValue}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    };

    const totalAnswers = platformAnswers.length + tenantAnswersList.length + jobAnswersList.length + aiAnswersList.length;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 pb-24 px-4">
          <div className="max-w-lg mx-auto space-y-4">

            <ResumeBanner />
            {/* Job Details */}
            {job && (
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {job.company.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm">{job.title}</h3>
                      <p className="text-slate-300 text-xs">{job.company} • {job.location}</p>
                    </div>
                    <Briefcase className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Platform Completion */}
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/80 text-sm">Profile Completion</span>
                  <span className="text-2xl font-bold">{platformPercentage}%</span>
                </div>
                <Progress value={platformPercentage} className="h-2 bg-white/20" />
                <p className="text-white/70 text-xs mt-2">
                  {answeredPlatform}/{platformQuestions.length} profile questions answered
                </p>
              </CardContent>
            </Card>

            {/* Phase 3 — no pre-submit match score; the score + skill-match
                report is shown after submit on the success page. */}

            {/* Parsed Data */}
            {parsedData && (parsedData.fullName || parsedData.email || parsedData.currentRole || parsedData.skills?.length) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Resume Data (Extracted)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {parsedData.fullName && (
                      <div><span className="text-muted-foreground">Name:</span> {parsedData.fullName}</div>
                    )}
                    {parsedData.email && (
                      <div><span className="text-muted-foreground">Email:</span> {parsedData.email}</div>
                    )}
                    {parsedData.mobile && (
                      <div><span className="text-muted-foreground">Mobile:</span> {parsedData.mobile}</div>
                    )}
                    {parsedData.currentRole && (
                      <div><span className="text-muted-foreground">Role:</span> {parsedData.currentRole}</div>
                    )}
                    {parsedData.currentCompany && (
                      <div><span className="text-muted-foreground">Company:</span> {parsedData.currentCompany}</div>
                    )}
                    {Number(parsedData.totalExperience) > 0 && (
                      <div><span className="text-muted-foreground">Experience:</span> {parsedData.totalExperience} years</div>
                    )}
                    {parsedData.location && (
                      <div className="col-span-2"><span className="text-muted-foreground">Location:</span> {parsedData.location}</div>
                    )}
                  </div>
                  {parsedData.skills && parsedData.skills.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">SKILLS ({parsedData.skills.length} found)</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {parsedData.skills.slice(0, 10).map((skill, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                        ))}
                        {parsedData.skills.length > 10 && (
                          <Badge variant="outline" className="text-xs">+{parsedData.skills.length - 10}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Skill Match */}
            {matchAnalysis && (matchAnalysis.matchedSkills.length > 0 || matchAnalysis.missingSkills.length > 0) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Skills Analysis</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  {matchAnalysis.matchedSkills.length > 0 && (
                    <div>
                      <span className="text-xs text-muted-foreground">MATCHED</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {matchAnalysis.matchedSkills.slice(0, 5).map((s, i) => (
                          <Badge key={i} className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {matchAnalysis.missingSkills.length > 0 && (
                    <div>
                      <span className="text-xs text-muted-foreground">TO DEVELOP</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {matchAnalysis.missingSkills.slice(0, 3).map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Answers Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Your Answers</span>
                  <Badge variant="outline">{totalAnswers} answered</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {renderCollapsibleSection('Profile Questions', 'platform', platformAnswers, platformQuestions, <User className="w-4 h-4 text-blue-500" />)}
                {renderCollapsibleSection('Company Questions', 'tenant', tenantAnswersList, tenantQuestions, <Briefcase className="w-4 h-4 text-green-500" />)}
                {renderCollapsibleSection('Job Questions', 'job', jobAnswersList, jobQuestions, <FileText className="w-4 h-4 text-amber-500" />)}
                {renderCollapsibleSection('AI Quiz Answers', 'ai', aiAnswersList, aiQuestions, <Sparkles className="w-4 h-4 text-purple-500" />)}
              </CardContent>
            </Card>

            {/* What Happens Next */}
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" /> What Happens Next?
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-medium">1.</span>
                    <span>Your application will be reviewed by the hiring team</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-medium">2.</span>
                    <span>If shortlisted, you&apos;ll receive an email for next steps</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-medium">3.</span>
                    <span>Track your application status in your dashboard</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Terms */}
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Checkbox id="terms" checked={acceptedTerms} onCheckedChange={(c) => setAcceptedTerms(c === true)} />
              <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                I agree to the <Link href="/legal/terms" className="text-primary hover:underline">Terms</Link> and{' '}
                <Link href="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              </Label>
            </div>

            {error && (
              <p
                role="alert"
                aria-live="polite"
                className="text-sm text-destructive"
              >
                {error}
              </p>
            )}
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="max-w-lg mx-auto flex gap-3">
            <Button variant="outline" onClick={() => setStep(aiQuestions.length > 0 ? 'ai_questions' : jobQuestions.length > 0 ? 'job_questions' : 'questions')}>Back</Button>
            <Button className="flex-1" size="lg" onClick={handleSubmit} disabled={!acceptedTerms || isLoading}>
              {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Submit Application <Send className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
